import { useContext, useState } from 'react';
import {
  ButtonToolbar,
  Dropdown,
  DropdownItem,
  DropdownToggle,
} from 'reactstrap';
import { FeatureCollection } from 'geojson';
import { bboxPolygon, featureCollection } from '@turf/turf';
import { captureEvent } from '../../utils/analytics';
import { MapContext } from '../../contexts/MapContext';
import { showError } from '../../utils/toast';
import loadSoilBoundaries from '../../utils/loadSoilBoundaries';
import { getMapBounds } from '../../utils/getMapBounds';
import ReplaceFeaturesModal from './buttons/ReplaceFeaturesModal';
import { UncontrolledWhiteTooltip } from '../basics/WhiteTooltip';
import SmallFontDropdownMenu from '../basics/SmallFontDropdownMenu';
import ChevronIcon from '../icons/ChevronIcon';

const MIN_ZOOM = 14;

const SoilLayerToolbar = () => {
  const {
    // @ts-expect-error MapContext not migrated to TS
    mapState: { mapRef, zoom },
    // @ts-expect-error MapContext not migrated to TS
    layers: { layers },
    // @ts-expect-error MapContext not migrated to TS
    soilAttributes: { resetSoilAttributes },
  } = useContext(MapContext);

  const [loadingForMap, setLoadingForMap] = useState(false);
  const [loadingForFarmBoundary, setLoadingForFarmBoundary] = useState(false);
  const [pendingFeatures, setPendingFeatures] = useState(featureCollection([]));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdownOpen = () => setDropdownOpen(!dropdownOpen);

  const disabled =
    loadingForMap ||
    loadingForFarmBoundary ||
    pendingFeatures?.features?.length > 0;
  const loadForMapDisabled = disabled || zoom < MIN_ZOOM;

  const hasFarmBoundary = layers.BOUNDARY?.data?.features?.length > 0;

  const fetchSoilBoundaries = async (
    boundingFeatures: FeatureCollection,
    setLoading: (loading: boolean) => void,
  ) => {
    if (!boundingFeatures?.features?.length) return;
    captureEvent('soil-boundaries-loaded');
    setLoading(true);
    try {
      const soilBoundaries = await loadSoilBoundaries(boundingFeatures);
      setPendingFeatures(soilBoundaries);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const onClickMapBounds = async () => {
    const [xMin, xMax, yMin, yMax] = getMapBounds(mapRef);
    const polygon = bboxPolygon([xMin, yMin, xMax, yMax]);
    delete polygon.bbox;
    return fetchSoilBoundaries(featureCollection([polygon]), setLoadingForMap);
  };

  const onClickFarmBoundary = async () =>
    fetchSoilBoundaries(layers.BOUNDARY.data, setLoadingForFarmBoundary);

  return (
    <ButtonToolbar className="align-items-center mr-2">
      <Dropdown isOpen={dropdownOpen} toggle={toggleDropdownOpen}>
        <DropdownToggle
          className="d-flex align-items-center"
          disabled={disabled}
          outline
          size="sm"
          title="Load Soil Types"
        >
          {disabled ? 'Loading...' : 'Load Soil Types'}
          <ChevronIcon className="ml-1" direction="down" />
        </DropdownToggle>
        <SmallFontDropdownMenu positionFixed>
          {hasFarmBoundary && (
            <DropdownItem disabled={disabled} onClick={onClickFarmBoundary}>
              Load for Farm Boundary
            </DropdownItem>
          )}
          {/* Put DropdownItem in span to enable tooltip on disabled item */}
          <span id="loadForCurrentView">
            <DropdownItem
              disabled={loadForMapDisabled}
              onClick={onClickMapBounds}
            >
              Load for Current View
            </DropdownItem>
          </span>
          {zoom < MIN_ZOOM && (
            <UncontrolledWhiteTooltip
              innerClassName="font-tiny"
              placement="bottom"
              target="loadForCurrentView"
            >
              Zoom in to load soil data for current view
            </UncontrolledWhiteTooltip>
          )}
        </SmallFontDropdownMenu>
      </Dropdown>
      <ReplaceFeaturesModal
        onCompleted={resetSoilAttributes}
        pendingFeatures={pendingFeatures}
        setPendingFeatures={setPendingFeatures}
      />
    </ButtonToolbar>
  );
};

export default SoilLayerToolbar;
